import MenuItem from '@mui/material/MenuItem';
import MuiSelect, { type SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import type { ResponsiveStyleValue } from '@mui/system';
import { useId } from 'react';

import Icon, { IconName } from '../../../icon/Icon';
import { TextFieldSize } from '../TextField/types';
import { useStyles } from './Dropdown.styles';

export type DropdownProps = {
  label?: React.ReactNode;
  options: readonly string[];
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  id?: string;
  className?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  inputSize?: ResponsiveStyleValue<TextFieldSize>;
  hideLabel?: boolean;
  disabled?: boolean;
  disablePortal?: boolean;
};

export default function Dropdown({
  label,
  options,
  value,
  onChange,
  onBlur,
  name,
  id,
  className,
  placeholder,
  helperText,
  error,
  fullWidth = false,
  inputSize = TextFieldSize.Large,
  hideLabel = false,
  disabled = false,
  disablePortal = false,
}: DropdownProps) {
  const { classes, cx } = useStyles({
    fullWidth,
    inputSize,
  });
  const autoId = useId();
  const fieldId = id ?? `dropdown-${autoId}`;
  const helperId = `${fieldId}-helper`;
  const labelId = `${fieldId}-label`;

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  return (
    <Stack className={cx(classes.root, className)}>
      {label && (
        <label
          id={labelId}
          htmlFor={fieldId}
          className={cx(classes.label, hideLabel && classes.labelHidden)}
        >
          {label}
        </label>
      )}
      <div className={classes.control}>
        <MuiSelect
          id={fieldId}
          labelId={label ? labelId : undefined}
          name={name}
          value={value}
          displayEmpty
          disabled={disabled}
          error={error}
          onChange={handleChange}
          onBlur={onBlur}
          className={classes.dropdown}
          inputProps={{
            'aria-labelledby': label ? labelId : undefined,
            'aria-describedby': helperText ? helperId : undefined,
            'data-testid': 'select-input',
          }}
          IconComponent={(iconProps) => (
            <Icon
              name={IconName.CHEVRON_DOWN}
              className={iconProps.className}
              width={16}
            />
          )}
          MenuProps={{
            disablePortal,
            disableScrollLock: false,
            keepMounted: true,
            className: classes.menu,
          }}
          renderValue={(selected) => (
            <span
              className={cx(
                classes.valueText,
                !selected && classes.placeholder
              )}
            >
              {selected || placeholder}
            </span>
          )}
        >
          {options.map((option, index) => (
            <MenuItem
              key={option}
              value={option}
              divider={index !== options.length - 1}
              data-testid={`menu-item-${index}`}
            >
              {option}
            </MenuItem>
          ))}
        </MuiSelect>
      </div>
      {helperText && (
        <div
          id={helperId}
          className={cx(classes.helper, error && classes.helperError)}
          role={error ? 'alert' : undefined}
        >
          {error ? (
            <>
              <Icon name={IconName.WARNING} width={18} />
              <span>{helperText}</span>
            </>
          ) : (
            helperText
          )}
        </div>
      )}
    </Stack>
  );
}
